import { useLegacyStore } from '@warp-drive/legacy';
import { JSONAPICache } from '@warp-drive/json-api';
import TioCachePolicy from './cache-policy.ts';
import { setIsMaybeMirage } from '@warp-drive/core/types/runtime';
import {
  createTioRequestHandlers,
  createTioRequestManager,
  type TioRequestManagerOptions,
} from './request-manager.ts';

type LegacyStoreOptions = Parameters<typeof useLegacyStore>[0];
type LegacySchemas = LegacyStoreOptions extends { schemas?: infer S } ? S : never;

setIsMaybeMirage(false);

/** Schemas and resource behavior registered with the TIO LegacyMode store. */
export interface TioSchemaManifest {
  schemas?: LegacySchemas;
  /** LegacyTrait objects (data/traits/*.schema.ts default exports). */
  traits?: unknown[];
  /** Extension registrations (data/resources/*.ext.ts default exports). */
  extensions?: unknown[];
}

/**
 * Creates the LegacyMode store shared by the TIO applications.
 *
 * @param options Static request-manager configuration established at boot.
 * @param manifest Schemas, traits, and resource extensions registered by the application.
 * @returns A configured Warp Drive Store class for Ember's store service registration.
 */
export default function createStore(
  options: TioRequestManagerOptions,
  manifest: TioSchemaManifest = {}
) {
  const { schemas = [], traits = [], extensions = [] } = manifest;

  const LegacyStore = useLegacyStore({
    legacyRequests: false,
    cache: JSONAPICache,
    // Soft expiry serves cached data, then background refresh until hard expiry; server cache headers may override these windows.
    policy: new TioCachePolicy({
      apiCacheSoftExpires: 5 * 60 * 1000, // 5 minutes
      apiCacheHardExpires: 30 * 60 * 1000, // 30 minutes
      constraints: {
        headers: {
          'X-WarpDrive-Expires': true,
          'Cache-Control': true,
          Expires: true,
        },
      },
    }),
    schemas,
    // LegacyMode consumes these registrations at runtime but omits them from its setup type.
    ...({
      traits,
      CAUTION_MEGA_DANGER_ZONE_extensions: extensions,
    } as Record<string, unknown>),
    handlers: createTioRequestHandlers(options),
    linksMode: false,
  });

  // The custom manager keeps demo interception ahead of LegacyMode's fixed handler ordering.
  class TioLegacyStore extends LegacyStore {
    requestManager = createTioRequestManager(options);
  }
  return TioLegacyStore;
}
